import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import BlockIcon from '@mui/icons-material/Block';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import cuentaCorrienteService, {
  type ComprobanteInterno,
} from '../../services/cuentaCorrienteService';
import PDFViewer from '../../components/shared/PDFViewer';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { formatComprobanteTesoreria, formatCurrency } from '../../utils/formatters';

const ESTADO_OPTIONS = ['activos', 'anulados', 'todos'] as const;
type FiltroEstado = (typeof ESTADO_OPTIONS)[number];
type FiltroTipo = '' | '80' | '81';

export type MovimientosInternosModo = 'clientes' | 'proveedores';

interface MovimientosInternosProps {
  modo?: MovimientosInternosModo;
}

const firstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
};

const lastDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
};

const esAnulado = (row: ComprobanteInterno) =>
  (row.asentado ?? '').trim().toUpperCase() === 'A' ||
  (row.impreso ?? '').trim().toUpperCase() === 'A' ||
  (row.nombre ?? '').trim().toUpperCase() === 'ANULADO';

const formatComprobante = (row: ComprobanteInterno): string =>
  formatComprobanteTesoreria(row.letra || 'X', row.nroSuc, row.nroComprobante);

const labelTipo = (nroTipo?: string) => {
  const t = (nroTipo ?? '').trim();
  if (t === '80') return 'Crédito interno';
  if (t === '81') return 'Débito interno';
  return t || '-';
};

const MovimientosInternosCxc: React.FC<MovimientosInternosProps> = ({ modo = 'clientes' }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const esClientes = modo === 'clientes';
  const listPath = esClientes ? '/ventas/movimientos-internos' : '/compras/movimientos-internos';
  const moduloPath = esClientes ? '/ventas' : '/compras';
  const moduloLabel = esClientes ? 'Ventas' : 'Compras';
  const terceroLabel = esClientes ? 'Cliente' : 'Proveedor';
  const creditoNuevo = `${listPath}/credito/nuevo`;
  const debitoNuevo = `${listPath}/debito/nuevo`;

  const [items, setItems] = useState<ComprobanteInterno[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [fechaDesde, setFechaDesde] = useState(firstDayOfMonth);
  const [fechaHasta, setFechaHasta] = useState(lastDayOfMonth);
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstado>('todos');
  const [filtroTipo, setFiltroTipo] = useState<FiltroTipo>('');
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [anulandoId, setAnulandoId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } =
    useSucursalesVentaFiltro({ resetPreferidaOnNavigation: true });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const nroSuc = resolveNroSucFiltro(filtroNroSuc);
      let asentado: string | undefined;
      if (filtroEstado === 'anulados') asentado = 'A';
      else if (filtroEstado === 'activos') asentado = 'activos';

      const response = await cuentaCorrienteService.listComprobantesInternos({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        tipoCliente: esClientes ? 'C' : 'P',
        searchTerm: searchTerm.trim() || undefined,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
        asentado,
        tipo: filtroTipo || undefined,
        nroSuc: nroSuc ?? undefined,
      });
      setItems(response.items ?? []);
      setTotalRecords(response.totalCount ?? 0);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; mensaje?: string } } };
      const msg =
        err?.response?.data?.mensaje ||
        err?.response?.data?.message ||
        'Error al cargar movimientos internos';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    searchTerm,
    fechaDesde,
    fechaHasta,
    filtroEstado,
    filtroTipo,
    filtroNroSuc,
    esClientes,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    if (!filtroListo) return;
    void fetchList();
  }, [fetchList, filtroListo, reloadKey]);

  const handleImprimir = async (nroFormulario: number) => {
    try {
      const blob = await cuentaCorrienteService.getComprobanteInternoPdfBlob(nroFormulario);
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedId(nroFormulario);
    } catch {
      enqueueSnackbar('Error al generar el PDF', { variant: 'error' });
    }
  };

  const handleClosePdf = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setSelectedId(null);
  };

  const handleAnular = async (row: ComprobanteInterno) => {
    if (esAnulado(row)) return;
    if (
      !window.confirm(
        '¿Anular este comprobante interno? Se revertirá el asiento contable asociado.'
      )
    ) {
      return;
    }
    setAnulandoId(row.nroFormulario);
    try {
      await cuentaCorrienteService.anularComprobanteInterno(row.nroFormulario);
      enqueueSnackbar('Comprobante interno anulado', { variant: 'success' });
      setReloadKey((k) => k + 1);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      enqueueSnackbar(err?.response?.data?.message || 'Error al anular', { variant: 'error' });
    } finally {
      setAnulandoId(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
            >
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(moduloPath)}
                  size="small"
                  sx={{ textTransform: 'none' }}
                >
                  {moduloLabel}
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Movimientos internos
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(creditoNuevo)}
                  sx={{ textTransform: 'none' }}
                >
                  Crédito interno
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<RemoveIcon />}
                  onClick={() => navigate(debitoNuevo)}
                  sx={{ textTransform: 'none' }}
                >
                  Débito interno
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder={`Buscar por número, comprobante o ${terceroLabel.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Desde"
                  InputLabelProps={{ shrink: true }}
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Hasta"
                  InputLabelProps={{ shrink: true }}
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1.5}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={filtroTipo}
                    label="Tipo"
                    onChange={(e) => {
                      setFiltroTipo(e.target.value as FiltroTipo);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="80">Crédito interno</MenuItem>
                    <MenuItem value="81">Débito interno</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={1.5}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={filtroEstado}
                    label="Estado"
                    onChange={(e) => {
                      setFiltroEstado(e.target.value as FiltroEstado);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="activos">Activos</MenuItem>
                    <MenuItem value="anulados">Anulados</MenuItem>
                    <MenuItem value="todos">Todos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => {
                    setFiltroNroSuc(v);
                    setPage(0);
                  }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Comprobante</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>{terceroLabel}</TableCell>
                    <TableCell align="right">Importe</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <CircularProgress size={28} sx={{ my: 3 }} />
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No hay movimientos internos para los filtros seleccionados.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => {
                      const anulado = esAnulado(row);
                      const anulando = anulandoId === row.nroFormulario;
                      return (
                        <TableRow key={row.nroFormulario} hover>
                          <TableCell>{formatComprobante(row)}</TableCell>
                          <TableCell>{labelTipo(row.nroTipoFormulario)}</TableCell>
                          <TableCell>
                            {row.fechaEmision
                              ? new Date(row.fechaEmision).toLocaleDateString('es-AR')
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {row.nroCliente} - {row.nombre}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.importeTotal ?? 0)}
                          </TableCell>
                          <TableCell>{row.usuario || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={anulado ? 'Anulado' : 'Activo'}
                              color={anulado ? 'default' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="Imprimir PDF">
                                <IconButton
                                  onClick={() => void handleImprimir(row.nroFormulario)}
                                  size="small"
                                  sx={{
                                    bgcolor: '#2196f3',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    width: 32,
                                    height: 32,
                                    '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {!anulado && (
                                <Tooltip title="Anular">
                                  <IconButton
                                    onClick={() => void handleAnular(row)}
                                    disabled={anulando}
                                    size="small"
                                    sx={{
                                      bgcolor: '#f44336',
                                      color: '#fff',
                                      borderRadius: 1.5,
                                      width: 32,
                                      height: 32,
                                      '&:hover': { bgcolor: '#d32f2f', transform: 'scale(1.1)' },
                                      '&:disabled': { bgcolor: '#ef9a9a', color: '#fff' },
                                      transition: 'all 0.2s',
                                    }}
                                  >
                                    {anulando ? (
                                      <CircularProgress size={16} sx={{ color: '#fff' }} />
                                    ) : (
                                      <BlockIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 20, 50]}
              labelRowsPerPage="Filas por página"
            />
          </Stack>
        </CardContent>
      </Card>

      {pdfUrl && selectedId != null && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePdf}
          title={`Comprobante interno ${selectedId}`}
          documentId={`${selectedId}`}
          documentType="informeCliente"
          downloadFileName={`comprobante_interno_${selectedId}.pdf`}
        />
      )}

    </Box>
  );
};

export default MovimientosInternosCxc;
